window.o_global = window.o_global || {};
window.o_util = window.o_util || {};

/**
 * Utility collection of miscellaneous core functions.
 *
 * @return {Object} Public methods of o_util.core.
 */
o_util.core = (function () {
    "use strict";

    /**
     * Binding function to its proper context, e.g. to handle events, timeouts etc.
     *
     * @example o_util.core.bind(fnThis, fn);
     *
     * @param {Object} fnThis       Context object that has to be bound.
     * @param {Function} fn         Function the context object will be bound to.
     *
     * @return {Function} Function with "magically" new "this"-context.
     */
    function bind(fnThis, fn) {
        return function () {
            return fn.apply(fnThis, arguments);
        };
    }

    /**
     * Convert a namespace string to an actually executable function object.
     *
     * @example
     * // Turn a namespace string to an executable function and execute it right away.
     * // Will virtually result in something like window.AS.RUM.cheers().
     * o_util.core.convertStringToFunction("AS.RUM.cheers", window)();
     *
     * @param {String} functionName     Namespace of the function to execute.
     * @param {Object} context          Context for the function, usually window.
     *
     * @return {Function} Returns the converted function.
     */
    function convertStringToFunction(functionName, context) {
        var namespaces = functionName.split("."),
            func = namespaces.pop(),
            i,
            len;

        for (i = 0, len = namespaces.length; i < len; i++) {
            context = context[namespaces[i]];
        }

        return context[func];
    }

    /**
     * Clones a given object. Necessary due to "copy by reference" nature of JavaScript.
     *
     * @example var newObject = o_util.core.clone(obj);
     *
     * @param {Object} object       Object that will be cloned.
     *
     * @return {Object} Actually the cloned object.
     */
    function clone(object) {
        if (!Object.create) { // @todo remove after we drop support for iOS 5.1
            Object.create = function (object) {
                /**
                 * Dummy function.
                 *
                 * @constructor
                 */
                function F() {
                }

                F.prototype = object;
                return new F();
            };
        }

        return Object.create(object);
    }

    /**
     * Merges two simple objects.
     *
     * @example
     * // Basic usage:
     * var mergedObj = o_util.core.extend(obj1, obj2);
     *
     * // Prevent properties of obj1 being overwritten by obj2.
     * var mergedObj = o_util.core.extend(obj1, obj2, false);
     *
     * // Merge objects by creating a true copy of obj1. obj1 will NOT be modified.
     * var mergedObj = o_util.core.extend(obj1, ob2, null, true);
     *
     * @param {Object} object1          Base object.
     * @param {Object} object2          Object that should be merged to obj1.
     * @param {Boolean} [overwrite]     Flag that determines if properties in obj1 will be overwritten with obj2. Default: true.
     * @param {Boolean} [cloneObj]      Determines if the original object will be cloned or not.
     *
     * @return {Object} Merged object consisting of properties of obj1 base object and obj2.
     */
    function extend(object1, object2, overwrite, cloneObj) {
        var replace = (typeof overwrite === "boolean") ? overwrite : true,
            doClone = (typeof cloneObj === "boolean") ? cloneObj : false,
            result = !!doClone ? clone(object1) : object1,
            key;

        for (key in object2) {
            /* istanbul ignore else */
            if (object2.hasOwnProperty(key)) {
                if (!replace && key in result) {
                    continue;
                }

                result[key] = object2[key];
            }
        }

        return result;
    }

    /**
     * Clone a function and all it's inherited properties.
     *
     * @deprecated Don't use this method anymore. Will be removed entirely.
     *
     * @example var func = o_util.core.cloneFunction(origFunc);
     *
     * @param {Function} functionReference      Function to be cloned.
     *
     * @return {Function} Cloned function.
     */
    function cloneFunction(functionReference) {
        var key;

        if (typeof functionReference !== "function") {
            return functionReference;
        }

        /**
         * Temporary function.
         *
         * @return {*}      Return value of the function.
         */
        function temp() {
            return functionReference.apply(null, arguments);
        }

        for (key in functionReference) {
            if (functionReference.hasOwnProperty(key)) {
                temp[key] = functionReference[key];
            }
        }

        return temp;
    }

    /**
     * Verify if given object is a plain JavaScript object.
     * Especially important to differentiate host objects from "plain objects" in legacy browsers where object prototype
     * of host objects is [object Object] instead of [object Window] for example.
     *
     * @param {Object} object   Value to prove.
     *
     * @return {Boolean} Is object (the function parameter) a plain JavaScript object?
     */
    function isPlainObject(object) {
        return !!object &&
            (Object.prototype.toString.call(object) === "[object Object]" && !object.nodeType && object !== object.window);
    }

    /**
     * Verify if given object is an empty JavaScript object (contains no enumerable properties).
     * Optionally use inherited properties from prototype chain.
     *
     * The argument should always be a plain JavaScript Object as other types of object (DOM elements, primitive strings/numbers,
     * host objects) may not give consistent results across browsers.
     *
     * @param {Object} object       Test object.
     * @param {Boolean} [inherited]    Use inheritence chain to check for properties. Default: false.
     *
     * @return {Boolean} Is object (the function parameter) an empty JavaScript object?
     */
    function isEmptyObject(object, inherited) {
        var properties = [];

        if (!inherited) {
            return Object.keys(object).length === 0;
        } else {
            /* jshint -W089 */
            for (var prop in object) {
                properties.push(prop);
            }

            return Object.keys(properties).length === 0;
        }
    }

    /**
     * Convert an array-like object, e.g. HTMLCollection or NodeList, to an actual array.
     *
     * @example
     * // Make the HTMLCollection returned by document.getElementsByClassName iterable.
     * var elements = o_util.core.toArray(document.getElementsByClassName("myClass"));
     *
     * elements.forEach(function(elem) {
     *     console.log(elem);
     * });
     *
     * @param {Object} object   Array-like object to convert to an array.
     *
     * @return {Array} An actual iterable Array.
     */
    function toArray(object) {
        var array = [];

        for (var i = object.length - 1; i >= 0; i--) {
            array[i] = object[i];
        }

        return array;
    }

    /**
     * Removes part of an Array.
     *
     * @example
     * var arr = ["a", "b", "c", "d", "e"];
     * o_util.core.removeFromArray(arr, 1, 3);
     * // arr -> ["a", "e"]
     *
     * @param {Array}   originalArray   Array from which the elements will be removed.
     * @param {Number}  from            Numeric index to start of part to remove.
     * @param {Number}  [to]            End of part to remove.
     *
     * @return {Array} Shrinked array which excludes the range of items that war deleted.
     */
    function removeFromArray(originalArray, from, to) {
        var rest = originalArray.slice((to || from) + 1 || originalArray.length);

        originalArray.length = from < 0 ? originalArray.length + from : from;
        originalArray.push.apply(originalArray, rest);

        return originalArray;
    }

    /**
     * Helper function for o_util.deserialize (type casting).
     *
     * @param {*} val The value to be coerced
     * @returns {*} A mixed, casted output
     */
    function coerce(val) {
        if (!isNaN(val)) {
            return Number(val);
        } else if (val === "true") {
            return true;
        } else if (val === "false") {
            return false;
        }

        return val;
    }

    /**
     * Transforms an object into an URL paramater format as string
     *
     * @param {Object} obj  The object to be serialized
     * @returns {String} The resulting, serialized object as string
     */
    function serialize(obj) {
        if (typeof obj === "object") {
            return Object.keys(obj).map(function (key) {
                return encodeURIComponent(key) + "=" + encodeURIComponent(obj[key]);
            }).join("&").replace(/%20/g, "+");
        }
    }

    /**
     * Transforms form data into an URL parameter format as string
     *
     * @param {Object} form The form to be serialized
     * @returns {String} The resulting, serialized formular data as string
     */
    function serializeForm(form) {
        var data = [];

        if (typeof form === "object" && form.nodeName === "FORM") {
            [].forEach.call(form.elements, function (element) {
                if (element.name && !element.disabled && element.type !== "submit" && element.type !== "reset" &&
                    element.type !== "button" && element.type !== "file") {
                    if (element.type === "select-multiple") {
                        [].forEach.call(element.options, function (option) {
                            if (option.selected) {
                                data[data.length] = encodeURIComponent(element.name) + "=" + encodeURIComponent(option.value);
                            }
                        });
                    } else if ((element.type !== "radio" && element.type !== "checkbox") || element.checked) {
                        data[data.length] = encodeURIComponent(element.name) + "=" + encodeURIComponent(element.value);
                    }
                }
            });
        }

        return data.join("&").replace(/%20/g, "+");
    }

    /**
     * Transforms a string in URL parameter format into an object
     *
     * @param {String}  str         The string to be deserialized
     * @param {Boolean} [coercion]  Indicator if value coercing is enabled (e.g. transforms "true" into boolean true)
     * @returns {Object} The resulting, deserialized object
     */
    function deserialize(str, coercion) {
        var decoded = decodeURIComponent(str.replace(/\+/g, "%20")),
            chunks = decoded.split("&"),
            params = {},
            key,
            val,
            splittedChunk;

        coercion = typeof coercion === "boolean" ? coercion : true;

        chunks.forEach(function (chunk) {
            splittedChunk = chunk.split("=");
            key = splittedChunk[0];
            val = coercion ? coerce(splittedChunk[1]) : splittedChunk[1];

            if (key !== "") {
                params[key] = val;
            }
        });

        return params;
    }

    /**
     * Return public API.
     */
    return {
        "bind": bind,
        "convertStringToFunction": convertStringToFunction,
        "extend": extend,
        "clone": clone,
        "cloneFunction": cloneFunction,
        "isEmptyObject": isEmptyObject,
        "isPlainObject": isPlainObject,
        "toArray": toArray,
        "removeFromArray": removeFromArray,
        "serialize": serialize,
        "serializeForm": serializeForm,
        "deserialize": deserialize,
        "coerce": coerce
    };
}());

// Namespace fallbacks.
o_global.helper = o_global.helper || {};
o_global.helper.bind = o_util.core.bind;
o_global.helper.clone = o_util.core.clone;
o_global.helper.cloneFunction = o_util.core.cloneFunction;
o_global.helper.convertStringToFunction = o_util.core.convertStringToFunction;
o_global.helper.extend = o_util.core.extend;
o_global.helper.removeFromArray = o_util.core.removeFromArray;
o_global.helper.isPlainObject = o_util.core.isPlainObject;
o_global.helper.isEmptyObject = o_util.core.isEmptyObject;
